import React, { useState, useRef, useEffect } from 'react';
import FormSelect from '../../Songs/components/FormSelect';
import en from '../../../translations/en.json';
import { toast } from '../../../components/shared/toast';
import './ArtistForm.css';

function ArtistForm({ artist, onSave, onCancel, isModal = false }) {
  const isEditMode = !!artist;
  const t = (key) => {
    return key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  };
  const getGenderValue = (g) => {
    if (g === 'M' || g === 'Male') return 'Male';
    if (g === 'F' || g === 'Female') return 'Female';
    if (g === 'O' || g === 'Other/Band' || g === 'Other/Group') return 'Other/Band';
    return '';
  };
  const [artistName, setArtistName] = useState(artist?.name || '');
  const [artistImage, setArtistImage] = useState(
    artist?.avatar ? { previewUrl: artist.avatar, name: 'current_avatar.png', size: 0 } : null
  );
  const [artistTypes, setArtistTypes] = useState({
    music: artist?.types?.includes('music') || false,
    lyrics: artist?.types?.includes('lyrics') || false,
    singer: artist?.types?.includes('singer') || false,
    band: artist?.types?.includes('band') || false,
    other: artist?.types?.includes('other') || false
  });
  const [gender, setGender] = useState(getGenderValue(artist?.gender));
  const [status, setStatus] = useState(
    artist ? (artist.status === 'Active' || artist.status === true || artist.status === 'true') : true
  );
  const [isDragActive, setIsDragActive] = useState(false);

  const fileInputRef = useRef(null);

  // Reset Form
  const handleReset = () => {
    setArtistName('');
    setArtistImage(null);
    setArtistTypes({
      music: false,
      lyrics: false,
      singer: false,
      band: false,
      other: false
    });
    setGender('');
    setStatus(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // File Upload Handlers
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const processFile = (file) => {
    const allowedExtensions = ['.png', '.jpg', '.jpeg'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (!allowedExtensions.includes(fileExt) && !file.type.includes('image')) {
      toast.warning('Artist Image must be PNG or JPG only.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setArtistImage({
        file: file,
        name: file.name,
        size: file.size,
        previewUrl: event.target.result
      });
    };
    reader.readAsDataURL(file);
  };

  const triggerFileSelect = () => {
    fileInputRef.current.click();
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleToggleChange = (type) => {
    setArtistTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Form validations using modern style warning notifications
    if (!artistName.trim()) {
      toast.warning('Artist Name is required.');
      return;
    }
    const typeSelected = Object.values(artistTypes).some(val => val === true);
    if (!typeSelected) {
      toast.warning('Please select at least one Artist Type.');
      return;
    }

    if (!gender) {
      toast.warning('Gender is required.');
      return;
    }

    const selectedTypes = Object.keys(artistTypes).filter(key => artistTypes[key]);

    const payload = {
      artistName: artistName.trim(),
      artistImage,
      artistTypes: selectedTypes,
      gender,
      status
    };

    onSave?.(payload);
  };

  const genderOptions = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other/Band', label: 'Other/Band' }
  ];

  return (
    <div className="artist-form-wrapper">
      {/* Page Header (Only visible if not inside modal) */}
      {!isModal && (
        <div className="artist-page-header">
          {/* Breadcrumb Navigation */}
          <nav className="breadcrumb-nav">
            <button type="button" className="breadcrumb-link" onClick={onCancel}>{t('songs.breadcrumb')}</button>
            <span className="breadcrumb-separator">&gt;</span>
            <span className="breadcrumb-current">{t('artists.breadcrumb')}</span>
          </nav>

          {/* Title Header Section */}
          <div className="add-song-header-row">
            <div className="header-icon-box">
              {/* User Avatar Circle Icon */}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div className="header-text-container">
              <h1 className="add-song-title">{t('artists.addNewTitle')}</h1>
              <p className="add-song-subtitle">{t('artists.addNewSubtitle')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Main Form Fields Container */}
      <form onSubmit={handleSubmit} className="artist-form">
        <div className="form-card">
          {/* Artist Name Input */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('artists.nameLabel')} <span className="required-asterisk">*</span>
            </label>
            <input
              type="text"
              className="form-text-input"
              placeholder={t('artists.namePlaceholder')}
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              disabled={isEditMode}
              style={isEditMode ? { backgroundColor: '#f1f5f9', cursor: 'not-allowed' } : undefined}
            />
          </div>

          {/* Artist Image Upload */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('artists.imageLabel')}
            </label>

            <input
              type="file"
              ref={fileInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {!artistImage ? (
              <div
                className={`artist-upload-zone ${isDragActive ? 'drag-active' : ''} ${isEditMode ? 'disabled' : ''}`}
                onDragEnter={isEditMode ? undefined : handleDrag}
                onDragOver={isEditMode ? undefined : handleDrag}
                onDragLeave={isEditMode ? undefined : handleDrag}
                onDrop={isEditMode ? undefined : handleDrop}
                onClick={isEditMode ? undefined : triggerFileSelect}
                style={isEditMode ? { cursor: 'not-allowed', opacity: 0.6 } : undefined}
              >
                <div className="artist-upload-inner">
                  {/* Cloud Icon */}
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#0b66e3" strokeWidth="2.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>

                  <span className="artist-upload-text">{t('artists.imageUploadInfo')}</span>

                  <button type="button" className="artist-upload-btn">
                    {t('common.chooseFile')}
                  </button>
                </div>
              </div>
            ) : (
              <div className="artist-image-preview-card">
                <div className="artist-image-preview-left">
                  <img src={artistImage.previewUrl} alt="Artist Preview" className="artist-preview-img" />
                  <div className="artist-preview-details">
                    <span className="artist-preview-filename">{artistImage.name}</span>
                    <span className="artist-preview-filesize">{formatFileSize(artistImage.size)}</span>
                  </div>
                </div>

                {!isEditMode && (
                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={() => setArtistImage(null)}
                    aria-label="Remove uploaded image"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Artist Type Toggles */}
          <div className="form-field-group">
            <label className="form-field-label">
              {t('artists.typeLabel')} <span className="required-asterisk">*</span>
            </label>

            <div className="artist-type-toggles-container">
              {['Music', 'Lyrics', 'Singer', 'Band', 'Other'].map(type => {
                const key = type.toLowerCase();
                const isChecked = artistTypes[key];

                return (
                  <label key={type} className={`artist-type-toggle-box ${isChecked ? 'active' : ''}`}>
                    <span className="type-switch">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleChange(key)}
                        disabled={isEditMode}
                      />
                      <span className="type-slider"></span>
                    </span>
                    <span className="type-label">{type}</span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Male or Female Dropdown */}
          <FormSelect
            label={t('artists.genderLabel')}
            required={true}
            options={genderOptions}
            value={gender}
            onChange={setGender}
            placeholder={t('artists.genderPlaceholder')}
            disabled={isEditMode}
          />

          {isEditMode && (
            <div className="form-field-group" style={{ marginTop: '16px' }}>
              <label className="form-field-label">Status</label>
              <div className="artist-type-toggles-container">
                <label className={`artist-type-toggle-box ${status ? 'active' : ''}`}>
                  <span className="type-switch">
                    <input
                      type="checkbox"
                      checked={status}
                      onChange={(e) => setStatus(e.target.checked)}
                    />
                    <span className="type-slider"></span>
                  </span>
                  <span className="type-label">{status ? 'Active' : 'Inactive'}</span>
                </label>
              </div>
            </div>
          )}

        </div>

        {/* Action Buttons */}
        <div className="artist-form-actions">
          {isModal ? (
            <>
              <button
                type="button"
                className="form-btn-outlined"
                onClick={onCancel}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="form-btn-primary"
              >
                {/* Save Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>{t('common.saveArtist')}</span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="form-btn-outlined"
                onClick={handleReset}
              >
                {t('common.reset')}
              </button>
              <button
                type="submit"
                className="form-btn-primary"
              >
                {/* Save Icon */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
                <span>{t('common.saveArtist')}</span>
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}

export default ArtistForm;
